import type { DramaCategory } from "../types/analysis";
import type {
	ResultModuleKey,
	ResultsExperiencePolicyOutput,
	ResultsScreenExperienceInputs,
	SharedLinkExperiencePolicyOutput,
} from "../types";
import { getBaseResultsModuleOrder } from "./resultsModuleRegistry";

function assertResultsSurface(inputs: ResultsScreenExperienceInputs): void {
	if (inputs.surface !== "results" && inputs.surface !== "shared_link") {
		throw new Error(
			`useResultsExperiencePolicy expected surface "results" or "shared_link", received "${inputs.surface}"`,
		);
	}
}

export function getLikelyImpactLabel(
	category: DramaCategory | null | undefined,
): string | null {
	switch (category) {
		case "safe":
			return "Calm";
		case "caution":
			return "Tense";
		case "high_drama":
			return "Defensive / tense";
		case "extreme_drama":
			return "Escalating";
		default:
			return null;
	}
}

function buildModuleOrder(
	renderVariant: "preview" | "full" | "generative",
	showLikelyImpact: boolean,
	showPrimaryRewriteCard: boolean,
	shareTreatment: "primary" | "secondary" | "hidden",
	showUpgradePrompt: boolean,
	hasPatterns: boolean,
	hasTransformations: boolean,
	includeFeedback: boolean,
): ResultModuleKey[] {
	return getBaseResultsModuleOrder(renderVariant).filter((moduleKey) => {
		switch (moduleKey) {
			case "likely_impact":
				return showLikelyImpact;
			case "primary_rewrite":
				return showPrimaryRewriteCard;
			case "patterns":
				return hasPatterns;
			case "transformations":
				return renderVariant !== "preview" && hasTransformations;
			case "feedback":
				return includeFeedback;
			case "share":
				return shareTreatment !== "hidden";
			case "upgrade_teaser":
				return showUpgradePrompt;
			default:
				return true;
		}
	});
}

function getResultsRenderVariant(
	inputs: ResultsScreenExperienceInputs,
): "preview" | "full" | "generative" {
	const isPreviewTier = inputs.entitlements.tierLevel <= 1;
	if (isPreviewTier) return "preview";
	if (
		inputs.surface === "results" &&
		inputs.features.generativeResultsEnabled
	) {
		return "generative";
	}
	return "full";
}

export function getResultsExperiencePolicy(
	inputs: ResultsScreenExperienceInputs,
): ResultsExperiencePolicyOutput | SharedLinkExperiencePolicyOutput {
	assertResultsSurface(inputs);

	const renderVariant = getResultsRenderVariant(inputs);
	const showLikelyImpact =
		inputs.result.hasDisplayableAnalysis &&
		!inputs.result.isCircuitBreakerResponse;
	const showPrimaryRewriteCard =
		inputs.features.rewritesEnabled &&
		inputs.result.hasDisplayableAnalysis &&
		inputs.result.hasRewrites;
	const shareTreatment =
		inputs.surface === "shared_link" || !inputs.features.sharedLinksEnabled
			? "hidden"
			: "primary";
	const showUpgradePrompt =
		inputs.surface === "results" &&
		renderVariant === "preview" &&
		Boolean(inputs.features.conversionUxEnabled);
	const primaryActionTreatment =
		inputs.surface === "results" ? "copy_use_primary" : "legacy";

	const moduleOrder = buildModuleOrder(
		renderVariant,
		showLikelyImpact,
		showPrimaryRewriteCard,
		shareTreatment,
		showUpgradePrompt,
		inputs.result.hasPatterns,
		inputs.result.hasTransformations,
		!inputs.result.isCircuitBreakerResponse,
	);

	if (inputs.surface === "shared_link") {
		return {
			surface: "shared_link",
			shellClassification: "foundation",
			renderVariant: renderVariant === "preview" ? "preview" : "full",
			showLikelyImpact,
			showPrimaryRewriteCard,
			primaryActionTreatment,
			showUpgradePrompt: false,
			shareTreatment,
			shouldUseLocalFallback: true,
			moduleOrder,
		};
	}

	return {
		surface: "results",
		shellClassification: "foundation",
		renderVariant,
		showLikelyImpact,
		showPrimaryRewriteCard,
		primaryActionTreatment,
		showUpgradePrompt: Boolean(showUpgradePrompt),
		shareTreatment,
		shouldUseLocalFallback: renderVariant !== "generative",
		moduleOrder,
	};
}

export function useResultsExperiencePolicy(
	inputs: ResultsScreenExperienceInputs,
): ResultsExperiencePolicyOutput | SharedLinkExperiencePolicyOutput {
	return getResultsExperiencePolicy(inputs);
}
